/**
 * Home, drawn to the reference.
 *
 * "Welcome back" with search and Create party across the top; the party you
 * are in, with its seats; Jump back in, big game art; Parties for you as cards
 * with who is already in them. Beside it: friends online, each with a way to
 * invite them, and your crew's next game night with I'm in.
 *
 * Everything shown is real - the parties, the seats, the people, the event -
 * and each part says so plainly when there is nothing to show.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { endpoints, api } from '@/lib/api';
import { go } from '@/lib/hash';
import { openPalette } from '@/lib/palette';
import { useSession } from '@/stores/session';
import { useVoice } from '@/stores/voice';
import { gameBanner, gameIconArt } from '@/lib/gameArt';
import { Rsvp } from '@/components/Rsvp';
import { Icon } from '@/icons/Icon';
import { cn } from '@/lib/cn';
import { Avatar } from '@/components/Avatar';
import { Tooltip } from '@/components/Tooltip';
import { InviteToParty } from '@/components/userMenu';
import { AddonSlot } from '@/components/Addons';
import type { Crew } from '@/components/Crew';
import type { Party, PublicUser } from '@/lib/types';

const PANEL = 'pugo-panel rounded-[calc(22px*var(--radius-mul,1))] bg-surface-1 shadow-[inset_0_0_0_1px_var(--line-soft)]';
const PRIMARY = cn(
  'flex items-center justify-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-accent font-bold text-accent-fg outline-none',
  'shadow-[0_8px_20px_-10px_color-mix(in_srgb,var(--accent)_85%,transparent)]',
  'transition-[background-color,transform] duration-[250ms] ease-[var(--ease-out)] hover:bg-accent-hi active:scale-[.98] focus-visible:shadow-[0_0_0_3px_var(--accent-soft)]'
);

export function HomeView() {
  const me = useSession((s) => s.me);
  const catalog = useSession((s) => s.catalog);

  const parties = useQuery({
    /* Shared with Games, which asks for exactly the same list. */
    queryKey: ['parties', 'all'],
    queryFn: () => endpoints.parties({ scope: 'all' }),
    staleTime: 15_000
  });
  const friends = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends, staleTime: 20_000 });

  const all = parties.data?.parties ?? [];
  const current = all.find((p) => !p.closed && p.isMember) ?? null;

  /* Only parties with a free seat - a full one must never look joinable. */
  const suggestions = useMemo(
    () => all.filter((p) => !p.closed && !p.isMember && p.memberCount < p.size && p.visibility === 'public').slice(0, 3),
    [all]
  );

  const known = useMemo(() => new Set((catalog?.games ?? []).map((g) => g.id)), [catalog]);
  const savedKey = (me?.games ?? []).map((g) => g.id).join(',');
  const recentGames = useMemo(() => {
    const byRecency = [...all]
      .filter((p) => p.gameId && !p.closed)
      .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
      .map((p) => p.gameId);
    const ordered = [...(savedKey ? savedKey.split(',') : []), ...byRecency, ...(catalog?.games ?? []).map((g) => g.id)];
    return [...new Set(ordered)].filter((id) => known.has(id) && id !== 'other').slice(0, 4);
  }, [all, savedKey, known, catalog]);

  const partyOf = useMemo(() => {
    const map = new Map<string, Party>();
    for (const p of all) if (!p.closed) for (const m of p.members ?? []) map.set(m.userId, p);
    return map;
  }, [all]);

  const online = (friends.data?.friends ?? []).filter((f) => f.user.status && f.user.status !== 'offline' && f.user.status !== 'invisible');
  const firstName = (me?.displayName || me?.username || '').split(/\s+/)[0] || 'there';

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex w-full max-w-[1480px] gap-6 px-4 py-6 sm:px-6 xl:px-8">

        {/* ================= main column ================= */}
        <div className="@container min-w-0 flex-1">
          <header className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-4 animate-[fade-in_280ms_ease-out] motion-reduce:animate-none">
            <div className="min-w-0 flex-1 basis-[260px]">
              <h1 className="m-0 flex items-center gap-3 text-[30px] leading-[1.1] font-extrabold tracking-[-.035em] text-ink @3xl:text-[36px]">
                <span className="truncate">Welcome back, {firstName}</span>
              </h1>
              <p className="mt-1.5 mb-0 text-[16px] text-mute">Your next great game starts here.</p>
            </div>

            <button
              type="button"
              onClick={openPalette}
              className="hidden h-12 min-w-[240px] flex-1 basis-[280px] items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-1 px-4 text-left text-[15px] text-ghost shadow-[inset_0_0_0_1px_var(--line)] outline-none transition-shadow duration-[250ms] hover:shadow-[inset_0_0_0_1px_var(--accent-line)] focus-visible:shadow-[0_0_0_2px_var(--accent)] @2xl:flex"
            >
              <Icon name="search" size={19} className="text-mute" />
              <span className="flex-1 truncate">Search games, friends or parties...</span>
              <kbd className="rounded-[7px] bg-surface-2 px-2 py-0.5 text-[12px] font-semibold text-mute shadow-[inset_0_0_0_1px_var(--line-soft)]">Ctrl K</kbd>
            </button>

            <button type="button" onClick={() => go('parties?create=1')} className={cn(PRIMARY, 'h-12 flex-none px-6 text-[15.5px]')}>
              <Icon name="plus" size={19} />
              Create party
            </button>
          </header>

          {current ? <PartyWaiting party={current} /> : null}

          {recentGames.length ? (
            <Section title="Jump back in" onSeeAll={() => go('games')}>
              <div className="grid grid-cols-2 gap-4 @3xl:grid-cols-4">
                {recentGames.map((id, i) => <GameTile key={id} gameId={id} name={catalog?.games.find((g) => g.id === id)?.name ?? id} index={i} />)}
              </div>
            </Section>
          ) : null}

          <Section title="Parties for you" onSeeAll={all.length ? () => go('parties') : undefined}>
            {parties.isPending ? (
              <div className="grid gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3">{[0, 1, 2].map((i) => <CardSkeleton key={i} />)}</div>
            ) : parties.isError ? (
              <Quiet title="Could not load parties" note="The list did not come back. Everything else is still working." action="Try again" onAction={() => void parties.refetch()} />
            ) : suggestions.length ? (
              <div className="grid gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3">
                {suggestions.map((p, i) => <PartyCard key={p.id} party={p} index={i} />)}
              </div>
            ) : (
              <Quiet
                title={current ? 'You are already in a party' : all.length ? 'Every party is full right now' : 'Nothing open at the moment'}
                note={current ? 'Leave it first if you want to join another one.' : 'Starting your own is the quickest way in - people see it on their home screen.'}
                action={current ? undefined : 'Create party'}
                onAction={() => go('parties?create=1')}
              />
            )}
          </Section>
        </div>

        {/* ================= rail ================= */}
        <aside className="hidden w-[330px] flex-none flex-col gap-5 lg:flex">
          <FriendsOnline online={online.map((f) => f.user)} pending={friends.isPending} partyOf={partyOf} />
          <GameNight />
          {/* Addons you have switched on run here too. */}
          <AddonSlot maxHeight={300} />
        </aside>
      </div>
      <style>{`@keyframes wave-hand { 0%,100% { transform: rotate(0) } 15% { transform: rotate(16deg) } 30% { transform: rotate(-8deg) } 45% { transform: rotate(14deg) } 60% { transform: rotate(-4deg) } 75% { transform: rotate(8deg) } }`}</style>
    </div>
  );
}

/* ------------------------------------------------------------------ parts */

function Section({ title, onSeeAll, children }: { title: string; onSeeAll?: () => void; children: React.ReactNode }) {
  return (
    <section className="mb-8 animate-[fade-in_320ms_ease-out] motion-reduce:animate-none">
      <div className="mb-3.5 flex items-center justify-between gap-3">
        <h2 className="m-0 text-[24px] font-bold tracking-[-.02em] text-ink">{title}</h2>
        {onSeeAll ? (
          <button type="button" onClick={onSeeAll} className="group flex items-center gap-1 rounded-[8px] px-2 py-1 text-[14.5px] font-medium text-dim outline-none transition-colors duration-[250ms] hover:text-accent focus-visible:shadow-[0_0_0_2px_var(--accent)]">
            See all
            <Icon name="chevronR" size={16} className="transition-transform duration-[250ms] group-hover:translate-x-0.5" />
          </button>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function useGameName(gameId: string | undefined): string {
  const catalog = useSession((s) => s.catalog);
  if (!gameId) return 'Game';
  return catalog?.games?.find((g) => g.id === gameId)?.name ?? gameId;
}

/** The party you are in, with its seats and the way back. */
function PartyWaiting({ party }: { party: Party }) {
  const art = gameBanner(party.gameId);
  const gameName = useGameName(party.gameId);
  const voice = useVoice();
  const inVoice = voice.partyId === party.id;
  const members = (party.members ?? []).filter((m) => m.user).slice(0, 4);
  const free = Math.max(0, Math.min(party.size, 4) - members.length);
  const up = members.filter((m) => m.user!.status !== 'offline' && m.user!.status !== 'invisible').length;

  return (
    <div className="mb-8 flex flex-wrap items-center gap-x-6 gap-y-4 rounded-[calc(22px*var(--radius-mul,1))] bg-[color-mix(in_srgb,var(--good)_9%,var(--bg-1))] p-4 shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--good)_30%,transparent)] animate-[fade-in_300ms_ease-out] motion-reduce:animate-none">
      <div className="smooth-clip h-[150px] w-[170px] flex-none overflow-hidden rounded-[calc(16px*var(--radius-mul,1))] bg-surface-3">
        {art ? <img src={art} alt="" className="h-full w-full object-cover" /> : null}
      </div>

      <div className="min-w-0 flex-1 basis-[280px]">
        <div className="flex items-center gap-2.5">
          <Icon name="friends" size={24} className="flex-none text-good" />
          <span className="text-[19px] font-bold text-ink">Your party is waiting</span>
        </div>
        <p className="mt-1 mb-4 text-[15px] text-mute">
          {up} of {party.size} online · {gameName}{inVoice ? ' · Voice connected' : ''}
        </p>
        <div className="flex flex-wrap items-start gap-5">
          {members.map((m) => (
            <span key={m.userId} className="flex w-[76px] flex-col items-center gap-2 text-center">
              <Avatar user={m.user!} size={64} presence />
              <span className="flex max-w-full items-center gap-1.5 truncate text-[13.5px] font-medium text-ink">
                <span aria-hidden className={cn('h-2 w-2 flex-none rounded-full', m.user!.status === 'offline' || m.user!.status === 'invisible' ? 'bg-ghost' : 'bg-good')} />
                <span className="truncate">{m.user!.displayName}</span>
              </span>
            </span>
          ))}
          {Array.from({ length: free }).map((_, i) => (
            <Tooltip key={i} content="An open seat. Invite a friend from the party.">
              <button type="button" onClick={() => go('party/' + party.id)} className="flex w-[76px] flex-col items-center gap-2 text-center outline-none">
                <span className="grid h-16 w-16 place-items-center rounded-full border-2 border-dashed border-line text-mute transition-colors duration-[250ms] hover:border-accent-line hover:text-accent">
                  <Icon name="plus" size={22} />
                </span>
                <span className="text-[13.5px] text-mute">Open seat</span>
              </button>
            </Tooltip>
          ))}
        </div>
      </div>

      <div className="flex flex-none flex-col items-center gap-2">
        <button type="button" onClick={() => go('party/' + party.id)} className={cn(PRIMARY, 'h-12 px-6 text-[15.5px]')}>
          <Icon name="headset" size={19} />
          Return to party
        </button>
        <span className="text-[13px] text-mute">Keep the adventure going!</span>
      </div>
    </div>
  );
}

function GameTile({ gameId, name, index }: { gameId: string; name: string; index: number }) {
  const art = gameBanner(gameId);
  return (
    <button
      type="button"
      onClick={() => go('parties?game=' + encodeURIComponent(gameId))}
      aria-label={'Browse ' + name + ' parties'}
      style={{ animationDelay: index * 50 + 'ms' }}
      className="group smooth-clip relative aspect-[16/11] overflow-hidden rounded-[calc(16px*var(--radius-mul,1))] bg-surface-3 text-left outline-none animate-[fade-in_320ms_ease-out_backwards] focus-visible:shadow-[0_0_0_3px_var(--accent)] motion-reduce:animate-none"
    >
      {art ? (
        <img src={art} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[400ms] ease-[var(--ease-out)] group-hover:scale-[1.05] motion-reduce:group-hover:scale-100" />
      ) : (
        <span aria-hidden className="absolute inset-0 grid place-items-center text-[26px] font-extrabold text-mute">{name.slice(0, 3).toUpperCase()}</span>
      )}
      <span aria-hidden className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent opacity-0 transition-opacity duration-[300ms] group-hover:opacity-100" />
      <span className="absolute bottom-3 left-3.5 translate-y-2 text-[15px] font-bold text-white opacity-0 transition-[opacity,transform] duration-[300ms] ease-[var(--ease-out)] group-hover:translate-y-0 group-hover:opacity-100">{name}</span>
      <span aria-hidden className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0_0_0_1px_rgba(255,255,255,.08)]" />
    </button>
  );
}

function PartyCard({ party, index }: { party: Party; index: number }) {
  const art = gameBanner(party.gameId);
  const icon = gameIconArt(party.gameId);
  const gameName = useGameName(party.gameId);
  const catalog = useSession((s) => s.catalog);
  const region = catalog?.regions.find((r) => r.id === party.region)?.name ?? party.region;
  const people = (party.members ?? []).filter((m) => m.user);

  return (
    <article
      style={{ animationDelay: index * 60 + 'ms' }}
      className={cn(PANEL, 'group smooth-clip flex flex-col overflow-hidden p-2.5 animate-[fade-in_320ms_ease-out_backwards] transition-[box-shadow,transform] duration-[250ms] ease-[var(--ease-out)] hover:-translate-y-0.5 hover:shadow-[inset_0_0_0_1px_var(--line),var(--shadow-2)] motion-reduce:animate-none motion-reduce:hover:translate-y-0')}
    >
      <div className="smooth-clip relative aspect-[16/8] flex-none overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] bg-surface-3">
        {art ? <img src={art} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[400ms] group-hover:scale-[1.04] motion-reduce:group-hover:scale-100" /> : null}
        {party.tags?.[0] ? (
          <span className="absolute top-2.5 right-2.5 rounded-[9px] bg-surface-1/95 px-2.5 py-1 text-[12.5px] font-semibold capitalize text-ink shadow-[var(--shadow-1)]">{party.tags[0]}</span>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-1 flex-col px-2 pt-3 pb-1.5">
        <div className="flex items-center gap-3">
          <span className="smooth-clip grid h-11 w-11 flex-none place-items-center overflow-hidden rounded-[calc(12px*var(--radius-mul,1))] bg-surface-3">
            {icon ? <img src={icon} alt="" className="h-full w-full object-cover" /> : <Icon name="gamepad" size={20} className="text-mute" />}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-[16px] font-bold text-ink">{gameName}</span>
            <span className="block truncate text-[13.5px] text-mute">{party.memberCount} / {party.size} seats{region ? ' · ' + region : ''}</span>
          </span>
        </div>
        <p className="mt-2.5 mb-3 line-clamp-2 min-h-[2.8em] text-[14px] leading-snug text-dim">{party.description || party.title}</p>
        <div className="mt-auto flex items-center gap-2">
          <span className="flex items-center">
            {people.slice(0, 3).map((m, i) => (
              <span key={m.userId} className="rounded-full ring-2 ring-[var(--bg-1)]" style={{ marginLeft: i ? -8 : 0 }}>
                <Avatar user={m.user!} size={32} />
              </span>
            ))}
            {people.length > 3 ? (
              <span className="grid h-8 min-w-8 place-items-center rounded-full bg-surface-3 px-1.5 text-[12px] font-semibold text-dim ring-2 ring-[var(--bg-1)]" style={{ marginLeft: -8 }}>+{people.length - 3}</span>
            ) : null}
          </span>
          <span className="flex-1" />
          <button type="button" onClick={() => go('party/' + party.id)} className={cn(PRIMARY, 'h-11 px-5 text-[15px]')}>Join party</button>
        </div>
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ rail */

function RailHead({ title, onSeeAll }: { title: string; onSeeAll?: () => void }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-2">
      <h2 className="m-0 text-[18px] font-bold tracking-[-.01em] text-ink">{title}</h2>
      {onSeeAll ? (
        <button type="button" onClick={onSeeAll} className="group flex items-center gap-0.5 rounded-[8px] px-1.5 py-0.5 text-[14px] font-medium text-accent outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]">
          See all
          <Icon name="chevronR" size={15} className="transition-transform duration-[250ms] group-hover:translate-x-0.5" />
        </button>
      ) : null}
    </div>
  );
}

function FriendsOnline({ online, pending, partyOf }: { online: PublicUser[]; pending: boolean; partyOf: Map<string, Party> }) {
  const catalog = useSession((s) => s.catalog);
  const [inviting, setInviting] = useState<PublicUser | null>(null);

  return (
    <section className={cn(PANEL, 'p-4 animate-[fade-in_300ms_ease-out] motion-reduce:animate-none')}>
      <RailHead title={'Friends online' + (pending ? '' : ' (' + online.length + ')')} onSeeAll={() => go('friends?tab=online')} />
      {pending ? (
        <div className="flex flex-col gap-3">{[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-[12px] bg-surface-2" />)}</div>
      ) : !online.length ? (
        <p className="m-0 py-1 text-[14px] text-mute">None of your friends are online right now.</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
          {online.slice(0, 7).map((user, i) => {
            const game = user.playing ? catalog?.games.find((g) => g.id === user.playing)?.name : null;
            const party = partyOf.get(user.id);
            const line = [game, party ? 'In a party' : game ? 'In game' : null].filter(Boolean).join(' · ') || 'Online';
            return (
              <li key={user.id} style={{ animationDelay: i * 35 + 'ms' }} className="group flex items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] p-1.5 animate-[fade-in_280ms_ease-out_backwards] transition-colors duration-[250ms] hover:bg-surface-2 motion-reduce:animate-none">
                <button type="button" onClick={() => go('u/' + user.username)} className="flex min-w-0 flex-1 items-center gap-3 text-left outline-none">
                  <Avatar user={user} size={46} presence />
                  <span className="min-w-0">
                    <span className="block truncate text-[15.5px] font-bold text-ink">{user.displayName}</span>
                    <span className="block truncate text-[13.5px] text-mute">{line}</span>
                  </span>
                </button>
                <Tooltip content={'Invite ' + user.displayName + ' to a party'}>
                  <button
                    type="button"
                    aria-label={'Invite ' + user.displayName + ' to a party'}
                    onClick={() => setInviting(user)}
                    className="grid h-9 w-9 flex-none place-items-center rounded-[10px] text-mute outline-none transition-[background-color,color] duration-[250ms] hover:bg-accent-soft hover:text-accent focus-visible:shadow-[0_0_0_2px_var(--accent)]"
                  >
                    <Icon name="gamepad" size={20} />
                  </button>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      )}
      {inviting ? <InviteToParty user={inviting} onClose={() => setInviting(null)} /> : null}
    </section>
  );
}

/** Your crew's next game night, with I'm in. */
function GameNight() {
  const me = useSession((s) => s.me);
  const catalog = useSession((s) => s.catalog);
  const tag = me?.crew?.tag ?? null;
  const crew = useQuery({
    queryKey: ['crew', tag],
    queryFn: () => api.get<{ crew: Crew }>('/api/crews/' + encodeURIComponent(tag!)),
    enabled: !!tag,
    staleTime: 30_000
  });
  const c = crew.data?.crew;
  const now = Date.now();
  const event = c?.events.find((e) => (e.endsAt ?? e.startsAt + 7200000) > now) ?? null;

  const art = event?.gameId ? gameBanner(event.gameId) : null;
  const when = event ? new Date(event.startsAt) : null;
  const gameName = event?.gameId ? catalog?.games.find((g) => g.id === event.gameId)?.name : null;

  return (
    <section className={cn(PANEL, 'p-4 animate-[fade-in_340ms_ease-out] motion-reduce:animate-none')}>
      <div className="mb-3 flex items-center gap-2.5">
        <Icon name="calendar" size={21} className="text-ink" />
        <h2 className="m-0 text-[18px] font-bold text-ink">Upcoming game night</h2>
      </div>
      {!tag ? (
        <div>
          <p className="m-0 text-[14px] text-mute">Game nights come from your crew. Join one and its events show up here.</p>
          <button type="button" onClick={() => go('crews?find=1')} className="mt-3 h-10 w-full rounded-[12px] bg-surface-2 text-[14px] font-semibold text-dim shadow-[inset_0_0_0_1px_var(--line-soft)] outline-none transition-colors hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]">Find a crew</button>
        </div>
      ) : crew.isPending ? (
        <div className="h-[220px] animate-pulse rounded-[14px] bg-surface-2" />
      ) : !event || !c ? (
        <div>
          <p className="m-0 text-[14px] text-mute">Nothing planned in {c?.name ?? 'your crew'} yet.</p>
          <button type="button" onClick={() => go('crews')} className="mt-3 h-10 w-full rounded-[12px] bg-surface-2 text-[14px] font-semibold text-dim shadow-[inset_0_0_0_1px_var(--line-soft)] outline-none transition-colors hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]">Open your crew</button>
        </div>
      ) : (
        <div>
          <button type="button" onClick={() => go('crews')} className="smooth-clip block aspect-[16/7] w-full overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] bg-surface-3 outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]">
            {art ? <img src={art} alt="" className="h-full w-full object-cover transition-transform duration-[400ms] hover:scale-[1.04]" /> : <span className="grid h-full place-items-center text-accent"><Icon name="calendar" size={34} /></span>}
          </button>
          <p className="mt-3 mb-0 flex items-center gap-2 text-[17px] font-bold text-ink">
            <span className="truncate">{event.title}</span>
            {event.live ? <span className="flex-none rounded-full bg-[color-mix(in_srgb,var(--bad)_16%,transparent)] px-2 py-px text-[12px] font-bold text-bad">Live</span> : null}
          </p>
          <p className="m-0 text-[14px] text-mute">
            {when!.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} · {when!.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </p>
          <p className="m-0 truncate text-[14px] text-mute">{[gameName ?? 'Any game', event.description || c.name].join(' · ')}</p>
          <div className="mt-3 flex items-center gap-2">
            <span className="flex items-center">
              {event.going.slice(0, 4).map((u, i) => (
                <span key={u.id} className="rounded-full ring-2 ring-[var(--bg-1)]" style={{ marginLeft: i ? -8 : 0 }}><Avatar user={u} size={32} /></span>
              ))}
              {event.goingCount > 4 ? <span className="grid h-8 min-w-8 place-items-center rounded-full bg-surface-3 px-1.5 text-[12px] font-semibold text-dim ring-2 ring-[var(--bg-1)]" style={{ marginLeft: -8 }}>+{event.goingCount - 4}</span> : null}
            </span>
            <span className="flex-1" />
            {c.myRole ? <Rsvp crewId={c.id} event={event} compact /> : null}
          </div>
        </div>
      )}
      <p className="mt-4 mb-0 flex items-center justify-center gap-2 border-t border-line-soft pt-3 text-[13px] text-mute">
        <Icon name="heart" size={14} />
        Good games. Better people.
        <Icon name="heart" size={14} />
      </p>
    </section>
  );
}

/* ---------------------------------------------------------------- states */

function CardSkeleton() {
  return (
    <div className={cn(PANEL, 'overflow-hidden p-2.5')}>
      <div className="aspect-[16/8] animate-pulse rounded-[14px] bg-surface-3" />
      <div className="flex flex-col gap-2 p-2.5">
        <div className="h-4 w-1/2 animate-pulse rounded bg-surface-3" />
        <div className="h-3 w-3/4 animate-pulse rounded bg-surface-3" />
        <div className="mt-2 h-11 animate-pulse rounded-[12px] bg-surface-3" />
      </div>
    </div>
  );
}

function Quiet({ title, note, action, onAction }: { title: string; note: string; action?: string; onAction?: () => void }) {
  return (
    <div className={cn(PANEL, 'p-8 text-center')}>
      <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-accent-soft text-accent"><Icon name="gamepad" size={26} /></span>
      <p className="mt-3 mb-0 text-[16px] font-semibold text-ink">{title}</p>
      <p className="mx-auto mt-1.5 mb-0 max-w-[400px] text-[14px] leading-relaxed text-mute">{note}</p>
      {action && onAction ? (
        <button type="button" onClick={onAction} className={cn(PRIMARY, 'mx-auto mt-4 h-11 px-5 text-[14.5px]')}>{action}</button>
      ) : null}
    </div>
  );
}
